/**
 * Коды ошибок и пользовательские тексты (§52).
 *
 * Правила:
 *  - Технические коды провайдеров сюда не попадают и наружу не уходят (§41).
 *  - Если ошибка связана с генерацией — текст обязан сообщать о судьбе единиц.
 *  - Фронт показывает `message` как есть; свободных строк в компонентах нет.
 */

export const ErrorCode = {
  // Платформа
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

  // Аккаунт и согласия (§4, §49)
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  EMAIL_ALREADY_USED: 'EMAIL_ALREADY_USED',
  CONSENT_REQUIRED: 'CONSENT_REQUIRED',
  ACCOUNT_BLOCKED: 'ACCOUNT_BLOCKED',

  // Экономика (§32-38)
  INSUFFICIENT_UNITS: 'INSUFFICIENT_UNITS',
  PAYMENT_FAILED: 'PAYMENT_FAILED',

  // Персона и генерация (§7, §22, §44)
  PERSONA_NOT_READY: 'PERSONA_NOT_READY',
  INPUT_QUALITY_LOW: 'INPUT_QUALITY_LOW',
  IDENTITY_ASSET_REQUIRED: 'IDENTITY_ASSET_REQUIRED',
  CAPTURE_TOO_LARGE: 'CAPTURE_TOO_LARGE',
  GENERATION_FAILED: 'GENERATION_FAILED',
  CONTENT_REJECTED: 'CONTENT_REJECTED',
  LIMIT_EXCEEDED: 'LIMIT_EXCEEDED',

  // Внешние сервисы — наружу отдаются уже как продуктовый текст (§41)
  PROVIDER_UNAVAILABLE: 'PROVIDER_UNAVAILABLE',
  PROVIDER_RATE_LIMITED: 'PROVIDER_RATE_LIMITED',
  PROVIDER_TIMEOUT: 'PROVIDER_TIMEOUT',
} as const;

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode];

/** Пользовательские тексты. Ни одного упоминания провайдеров и технических деталей. */
export const errorMessages: Record<ErrorCodeValue, string> = {
  VALIDATION_ERROR: 'Проверьте заполненные поля.',
  UNAUTHORIZED: 'Войдите, чтобы продолжить.',
  FORBIDDEN: 'Нет доступа к этому разделу.',
  NOT_FOUND: 'Не найдено.',
  CONFLICT: 'Это действие уже выполнено.',
  RATE_LIMITED: 'Слишком много попыток. Попробуйте чуть позже.',
  INTERNAL_ERROR: 'Что-то пошло не так. Мы уже разбираемся.',
  SERVICE_UNAVAILABLE: 'Сервис временно недоступен. Попробуйте позже.',

  INVALID_CREDENTIALS: 'Неверная почта или пароль.',
  EMAIL_NOT_VERIFIED: 'Подтвердите почту, чтобы продолжить.',
  EMAIL_ALREADY_USED: 'Эта почта уже используется.',
  CONSENT_REQUIRED: 'Нужно принять условия, чтобы продолжить.',
  ACCOUNT_BLOCKED: 'Доступ к аккаунту ограничен. Напишите в поддержку.',

  INSUFFICIENT_UNITS: 'Не хватает единиц. Пополните баланс.',
  PAYMENT_FAILED: 'Платёж не прошёл. Попробуйте ещё раз.',

  PERSONA_NOT_READY: 'Персона ещё не готова. Дождитесь завершения подготовки.',
  INPUT_QUALITY_LOW:
    'В исходном видео слишком много фонового шума. Запишите его ещё раз или отдельно дополните голос.',
  IDENTITY_ASSET_REQUIRED: 'Для этой сцены понадобится дополнительная фотография.',
  CAPTURE_TOO_LARGE: 'Запись слишком длинная. Запишите ролик покороче — до минуты достаточно.',
  GENERATION_FAILED: 'Не удалось создать сцену. Единицы не списаны.',
  CONTENT_REJECTED: 'Такое содержание создать не получится. Попробуйте изменить описание.',
  LIMIT_EXCEEDED: 'Достигнут лимит. Попробуйте уменьшить объём или повторить позже.',

  PROVIDER_UNAVAILABLE: 'Сервис временно недоступен. Единицы не списаны, попробуйте позже.',
  PROVIDER_RATE_LIMITED: 'Сейчас высокая нагрузка. Мы повторим попытку автоматически.',
  PROVIDER_TIMEOUT: 'Не удалось создать сцену. Единицы не списаны.',
};

/** HTTP-статусы по кодам. Всё, чего нет в карте, — 500. */
export const errorHttpStatus: Record<ErrorCodeValue, number> = {
  VALIDATION_ERROR: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  RATE_LIMITED: 429,
  INTERNAL_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,

  INVALID_CREDENTIALS: 401,
  EMAIL_NOT_VERIFIED: 403,
  EMAIL_ALREADY_USED: 409,
  CONSENT_REQUIRED: 403,
  ACCOUNT_BLOCKED: 403,

  INSUFFICIENT_UNITS: 402,
  PAYMENT_FAILED: 402,

  PERSONA_NOT_READY: 409,
  INPUT_QUALITY_LOW: 422,
  IDENTITY_ASSET_REQUIRED: 422,
  CAPTURE_TOO_LARGE: 422,
  GENERATION_FAILED: 500,
  CONTENT_REJECTED: 422,
  LIMIT_EXCEEDED: 429,

  PROVIDER_UNAVAILABLE: 503,
  PROVIDER_RATE_LIMITED: 503,
  PROVIDER_TIMEOUT: 504,
};

export function messageForCode(code: string): string {
  return errorMessages[code as ErrorCodeValue] ?? errorMessages.INTERNAL_ERROR;
}

export function httpStatusForCode(code: string): number {
  return errorHttpStatus[code as ErrorCodeValue] ?? 500;
}
